import React, { useEffect, useState } from 'react';
import { Section as SectionDI, SkeletonItem as SkeletonItemDI } from '@atlaskit/side-navigation';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { expVal, UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { ExpandableSubmenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/expandable-submenu-item/index.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import {
	NextGenIssueTypesWrapper,
	ExpandableProjectSettingsWrapper,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/navigation-component-wrappers/index.tsx';
import { useBoard as useBoardDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { useProject as useProjectDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { NestingMenuItem as NestingMenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import { NotificationsSubMenu } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/notifications-sub-menu/index.tsx';
import { shouldRenderNesting } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/should-render/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { matchUrlPathname as matchUrlPathnameDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { useFeatures as useFeaturesDI } from '@atlassian/jira-navigation-apps-sidebar-next-gen-use-features/src/controllers/features/index.tsx';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import {
	ConnectAppsContainer,
	useConnectApps as useConnectAppsDI,
} from '@atlassian/jira-project-settings-apps-connect-apps/src/baskets/connect-apps/index.tsx';
import { useJsmContextWithLoadOnMount } from '@atlassian/jira-router-resources-service-desk-jsm-context/src/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useRouter, type JiraSpaRoute } from '@atlassian/react-resource-router';
import { SECTION_ID } from '../../../common/constants/index.tsx';
import { useNextgenSidebarSettings } from '../../../controllers/settings/index.tsx';
import { BoardMenu } from './board/index.tsx';
import { EcosystemAppsMenu as EcosystemAppsMenuDI } from './ecosystem-apps/main.tsx';
import { messages as ecosystemAppsMessages } from './ecosystem-apps/messages.tsx';
import { IssueTypes } from './issue-types/index.tsx';
import { messages } from './messages.tsx';
import { ToolchainMenuItem } from './toolchain-menu-item/index.tsx';
import type { SettingsProps } from './types.tsx';

const useThrowProjectDI = withThrowOnError(useProjectDI);
const useThrowBoardDI = withThrowOnError(useBoardDI);
const useThrowFeaturesDI = withThrowOnError(useFeaturesDI);
const useThrowSidebarSettingsDI = withThrowOnError(useNextgenSidebarSettings);

export const Settings = ({
	EcosystemAppsMenu = EcosystemAppsMenuDI,
	MenuItem = MenuItemDI,
	NestingMenuItem = NestingMenuItemDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	matchUrlPathname = matchUrlPathnameDI,
	stack,
	useConnectApps = useConnectAppsDI,
	useIntl = useIntlDI,
	useProject = useThrowProjectDI,
	useBoard = useThrowBoardDI,
	useFeatures = useThrowFeaturesDI,
	useSidebarSettings = useThrowSidebarSettingsDI,
}: SettingsProps) => {
	const { formatMessage } = useIntl();
	const { data: project } = useProject();
	const { data: board, loading: boardLoading } = useBoard();
	const { data: features } = useFeatures();
	const [{ route }] = useRouter();
	const { data: navSettings } = useSidebarSettings(features);

	const calculateBoardsActive = ({ name }: JiraSpaRoute) =>
		name === 'project-settings-software-boards-csm';

	const calculateNotificationsActive = ({ name }: JiraSpaRoute) =>
		name === 'project-settings-software-notifications' ||
		name === 'project-settings-software-notifications-issue-logs';

	const calculateAppsActive = ({ name }: JiraSpaRoute) =>
		name.includes('project-settings-software-apps') ||
		name.includes('project-settings-software-link-opsgenie');

	const {
		[LICENSED_PRODUCTS.OPSGENIE]: hasOpsgenieLicense,
		[LICENSED_PRODUCTS.JIRA_SERVICEDESK]: hasServicedeskLicence,
	} = useLicensedProducts();
	const { data: jsmContext } = useJsmContextWithLoadOnMount();
	const hasOpsgenie = hasServicedeskLicence
		? !!(jsmContext?.opsgenieStatus && jsmContext.opsgenieStatus !== 'NONE')
		: hasOpsgenieLicense;
	const [{ data: connectAppsData }, { fetch: fetchConnectApps }] = useConnectApps();

	const [boardsActive] = useState<boolean>(calculateBoardsActive(route));
	const [notificationsActive] = useState<boolean>(calculateNotificationsActive(route));
	const [appsActive, setAppsActive] = useState<boolean>(calculateAppsActive(route));

	// to remove on isAppControlsEnabled() cleanup
	useEffect(() => {
		if (project && !connectAppsData) fetchConnectApps(project.key);
	}, [connectAppsData, fetchConnectApps, project]);

	useEffect(() => {
		setAppsActive(calculateAppsActive(route));
	}, [route]);

	if (features == null || project == null || (board == null && boardLoading)) {
		return <SkeletonItem />;
	}

	const baseSettingsUrl = getNextgenSettingsUrl(SOFTWARE_PROJECT, project.key);

	const BackMenuItem = {
		href: baseSettingsUrl,
		title: formatMessage(messages.title),
	};

	const [expConfig] = UNSAFE_noExposureExp('efa_and_project_fields_page_for_tmp');

	return (
		<ExpandableProjectSettingsWrapper>
			<Section>
				{navSettings?.shouldShowDetailsMenu && (
					<MenuItem
						analytics={{ itemId: 'details' }}
						selectedOn={matchUrlPathname()}
						href={`${baseSettingsUrl}/details`}
					>
						{formatMessage(messages.itemDetails)}
					</MenuItem>
				)}
				{navSettings?.shouldShowAccessMenu && (
					<MenuItem
						analytics={{ itemId: 'access' }}
						href={`${baseSettingsUrl}/access`}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.itemAccess)}
					</MenuItem>
				)}
				{navSettings?.shouldShowNotificationMenu && (
					<ExpandableSubmenuItem
						analytics={{ itemId: 'notifications' }}
						menuItemLabel={formatMessage(messages.itemNotifications)}
						forceExpand={notificationsActive}
						submenuId="nextgen-notifications-settings"
					>
						<NotificationsSubMenu
							settingsUrl={`${baseSettingsUrl}/notifications`}
							issueLogsUrl={`${baseSettingsUrl}/notifications/issue-logs?projectId=${project.id}`}
						/>
					</ExpandableSubmenuItem>
				)}
				{navSettings?.shouldShowAutomationMenu && (
					<MenuItem
						analytics={{ itemId: 'automation' }}
						selectedOn={matchUrlPathname()}
						href={`${baseSettingsUrl}/automation`}
					>
						{formatMessage(messages.automation)}
					</MenuItem>
				)}
				{navSettings?.shouldShowFieldsMenu && expConfig.get('isEnabled', false) && (
					<MenuItem
						analytics={{ itemId: 'fields' }}
						selectedOn={matchUrlPathname()}
						href={`${baseSettingsUrl}/fields`}
					>
						{formatMessage(messages.itemFieldsNonFinal)}
					</MenuItem>
				)}
				{shouldRenderNesting(
					navSettings?.shouldShowIssueTypesMenu,
					stack,
					SECTION_ID.ISSUE_TYPES,
				) && (
					<NestingMenuItem
						analytics={{ itemId: 'issuetypes' }}
						href={`${baseSettingsUrl}/issuetypes`}
						id={SECTION_ID.ISSUE_TYPES}
						overrides={{ BackMenuItem }}
						title={formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.itemIssueTypesIssueTermRefresh
								: messages.itemIssueTypes,
						)}
					>
						<NextGenIssueTypesWrapper submenuActive nestedAppOpen>
							<IssueTypes />
						</NextGenIssueTypesWrapper>
					</NestingMenuItem>
				)}
				{navSettings?.shouldShowFeaturesMenu && (
					<MenuItem
						analytics={{ itemId: 'features' }}
						href={`${baseSettingsUrl}/features`}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.itemFeatures)}
					</MenuItem>
				)}
				{navSettings?.shouldShowBoardsMenu && board != null && (
					<ExpandableSubmenuItem
						analytics={{ itemId: 'boards' }}
						menuItemLabel={formatMessage(messages.itemBoards)}
						forceExpand={boardsActive}
						submenuId="nextgen-boards-setttings"
					>
						<BoardMenu />
					</ExpandableSubmenuItem>
				)}
				{navSettings?.shouldShowProformaFormsMenu && fg('proforma_mvp_jsw_project_settings') && (
					<MenuItem
						analytics={{ itemId: 'proforma-forms' }}
						selectedOn={matchUrlPathname()}
						href={`${baseSettingsUrl}/forms`}
					>
						{expVal('forms_for_jsw_experiment', 'isEnabled', false)
							? formatMessage(messages.itemAdvancedForms)
							: formatMessage(messages.itemForms)}
					</MenuItem>
				)}
				{navSettings?.shouldShowToolchainMenu && (
					<ToolchainMenuItem projectId={project.id} projectKey={project.key} />
				)}
				{shouldRenderNesting(navSettings?.shouldShowEcosystemAppMenu, stack, 'apps') && (
					<ExpandableSubmenuItem
						analytics={{ itemId: 'apps' }}
						menuItemLabel={formatMessage(ecosystemAppsMessages.apps)}
						forceExpand={appsActive}
						submenuId="nextgen-apps-setttings"
					>
						<EcosystemAppsMenu>
							{hasOpsgenie && (
								<MenuItem
									analytics={{ itemId: 'opsgenie' }}
									href={`${baseSettingsUrl}/opsgenie`}
									selectedOn={matchUrlPathname()}
								>
									{formatMessage(messages.itemOpsgenie)}
								</MenuItem>
							)}
						</EcosystemAppsMenu>
					</ExpandableSubmenuItem>
				)}
				{navSettings?.shouldShowDlpMenu && (
					<MenuItem
						analytics={{ itemId: 'data-classification' }}
						href={`${baseSettingsUrl}/data-classifications`}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.dataClassificationNonFinal)}
					</MenuItem>
				)}
			</Section>
		</ExpandableProjectSettingsWrapper>
	);
};

export const SettingsMenu = ({
	SkeletonItem = SkeletonItemDI,
	useProject = useProjectDI,
	...props
}: SettingsProps) => {
	const { data: project } = useProject();

	if (project == null) return <SkeletonItem />;

	return (
		// to remove on isAppControlsEnabled() cleanup
		<ConnectAppsContainer onError={undefined} scope={project.key}>
			<Settings {...props} />
		</ConnectAppsContainer>
	);
};
